/**
 * Stocktake sessions (#3031): API types, query hooks and the pure rules the
 * counting UI shares with the server (bounds, reasons, who may approve).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

export const STOCKTAKE_STATUSES = ['planned', 'counting', 'submitted', 'approved', 'cancelled'] as const;
export type StocktakeStatus = (typeof STOCKTAKE_STATUSES)[number];
export type StocktakeKind = 'planned' | 'ad_hoc';

export const STOCKTAKE_REASON_CODES = [
  'count_error',
  'damaged',
  'expired',
  'theft_loss',
  'found',
  'wrong_location',
  'unrecorded_usage',
  'supplier_error',
  'other',
] as const;
export type StocktakeReasonCode = (typeof STOCKTAKE_REASON_CODES)[number];

/** Same bound the API enforces on one counted quantity. */
export const MAX_COUNTED_QUANTITY = 1_000_000;
/** Same bound the API enforces on one counts request. */
export const MAX_COUNTS_PER_REQUEST = 200;
/** Lines fetched per page of a session; more are loaded on request. */
export const LINES_PAGE_SIZE = 200;
/** Sessions fetched per page of the list. */
export const SESSIONS_PAGE_SIZE = 50;

export interface StocktakeSession {
  id: string;
  name: string;
  kind: StocktakeKind;
  status: StocktakeStatus;
  plannedFor: string | null;
  warehouseId: string;
  binLocationId: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  startedBy: string | null;
  startedAt: string | null;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalNote: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export interface StocktakeLine {
  id: string;
  partId: string;
  partName: string;
  sku: string;
  barcode: string | null;
  unit: string;
  warehouseId: string;
  binLocationId: string | null;
  binLocationCode: string | null;
  snapshotOnHand: number;
  snapshotReserved: number;
  countedQuantity: number | null;
  onHandAtCount: number | null;
  countedBy: string | null;
  countedAt: string | null;
  reasonCode: StocktakeReasonCode | null;
  note: string | null;
  version: number;
  resolution: string;
  appliedDelta: number | null;
}

export type StocktakeLineFilter = 'all' | 'pending' | 'counted' | 'discrepancy';

export interface StocktakeDetail {
  session: StocktakeSession;
  progress: { total: number; counted: number; discrepancies: number };
  people: Record<string, string>;
  /**
   * Everyone who counted a line of the session, across all pages. Optional so
   * a portal talking to an API from before this field still renders.
   */
  counters?: string[];
  lines: { data: StocktakeLine[]; total: number; limit: number; offset: number };
}

export type ReportLineState = 'uncounted' | 'match' | 'discrepancy' | 'review' | 'blocked';

/**
 * Where a report line's stock and cost come from: the stock row now (`live`),
 * what approval stored (`stored`), or today's values for an approved line that
 * approval did not write (`live_reference`).
 */
export type ReportBasis = 'live' | 'stored' | 'live_reference';

export interface StocktakeReportLine {
  lineId: string;
  partId: string;
  partName: string;
  sku: string;
  unit: string;
  binLocationCode: string | null;
  snapshotOnHand: number;
  countedQuantity: number | null;
  onHandAtCount: number | null;
  currentOnHand: number;
  currentReserved: number;
  discrepancy: number | null;
  movedSinceCount: number | null;
  unitCostCents: number;
  discrepancyValueCents: number | null;
  reasonCode: StocktakeReasonCode | null;
  note: string | null;
  state: ReportLineState;
  blockedCode: 'NEGATIVE_QUANTITY' | 'BELOW_HELD_STOCK' | null;
  resolution: string;
  basis: ReportBasis;
}

export interface StocktakeReport {
  session: StocktakeSession;
  lines: StocktakeReportLine[];
  summary: {
    lines: number;
    counted: number;
    uncounted: number;
    matching: number;
    discrepancyLines: number;
    reviewRequired: number;
    blocked: number;
    applied: number;
    rejected: number;
    skipped: number;
    surplusValueCents: number;
    shortageValueCents: number;
    netValueCents: number;
    unappliedValueCents: number;
  };
}

export interface CountEntry {
  lineId: string;
  countedQuantity: number;
  unit: string;
  expectedVersion: number;
  reasonCode?: StocktakeReasonCode | null;
  note?: string | null;
}

export interface CreateStocktakeInput {
  name: string;
  kind: StocktakeKind;
  plannedFor?: string;
  warehouseId: string;
  binLocationId?: string | null;
}

export interface ApproveStocktakeInput {
  note?: string;
  acknowledgedLineIds: string[];
  rejectedLineIds: string[];
}

export interface ApproveStocktakeResult {
  session: StocktakeSession;
  replayed: boolean;
  applied: number;
  unchanged: number;
  rejected: number;
}

/** MUI chip colour of a session status. */
export const STATUS_COLOR: Record<StocktakeStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  planned: 'default',
  counting: 'info',
  submitted: 'warning',
  approved: 'success',
  cancelled: 'error',
};

// ─── Pure rules ──────────────────────────────────────────────────────────────

export type QuantityParse = { ok: true; value: number } | { ok: false };

/** A counted quantity is a whole number from 0 to the API bound; anything else is refused before sending. */
export function parseCountedQuantity(input: string): QuantityParse {
  const trimmed = input.trim();
  if (!/^\d{1,7}$/.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  return value <= MAX_COUNTED_QUANTITY ? { ok: true, value } : { ok: false };
}

/** Counted minus the book quantity the server read when the count was recorded. */
export function lineDiscrepancy(line: Pick<StocktakeLine, 'countedQuantity' | 'onHandAtCount'>): number | null {
  if (line.countedQuantity === null || line.onHandAtCount === null) return null;
  return line.countedQuantity - line.onHandAtCount;
}

/**
 * Book quantity a draft count is compared with: what the server read at the
 * last count, or the snapshot for a line that was never counted.
 */
export function bookQuantity(line: Pick<StocktakeLine, 'onHandAtCount' | 'snapshotOnHand'>): number {
  return line.onHandAtCount ?? line.snapshotOnHand;
}

/** A discrepancy needs a reason; "other" also needs a note. Mirrors the server's submit check. */
export function reasonMissing(
  difference: number,
  reasonCode: StocktakeReasonCode | null,
  note: string,
): boolean {
  if (difference === 0) return false;
  if (reasonCode === null) return true;
  return reasonCode === 'other' && note.trim() === '';
}

/** A real calendar date as YYYY-MM-DD (the API refuses 2027-02-29). */
export function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1) return false;
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function canEditCounts(session: Pick<StocktakeSession, 'status'>): boolean {
  return session.status === 'counting';
}

/**
 * The server refuses approval by anyone who counted or submitted the session.
 * The UI hides the button for both, so the refusal is not a surprise.
 * `counters` is the session-wide list from the detail response, not the lines
 * on screen (those may be one page or one filter of the session).
 */
export function canApprove(
  session: Pick<StocktakeSession, 'status' | 'submittedBy'>,
  userId: string | null,
  mayAdjust: boolean,
  counters: readonly string[] = [],
): boolean {
  return (
    mayAdjust &&
    session.status === 'submitted' &&
    userId !== null &&
    session.submittedBy !== userId &&
    !counters.includes(userId)
  );
}

/**
 * Saved lines the server will refuse at submit for a missing reason: the
 * difference is the server's (counted minus the book quantity it read when
 * the count was saved), not the counter's local guess.
 */
export function linesMissingReason(lines: readonly StocktakeLine[]): string[] {
  return lines
    .filter(
      (line) =>
        line.countedQuantity !== null &&
        line.onHandAtCount !== null &&
        reasonMissing(line.countedQuantity - line.onHandAtCount, line.reasonCode, line.note ?? ''),
    )
    .map((line) => line.id);
}

export function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const data = error.data;
  if (typeof data === 'object' && data !== null && 'code' in data && typeof data.code === 'string') {
    return data.code;
  }
  return null;
}

export function apiErrorDetails<T>(error: unknown): T | null {
  if (!(error instanceof ApiError)) return null;
  const data = error.data;
  if (typeof data === 'object' && data !== null && 'details' in data) return data.details as T;
  return null;
}

/** Keeps the first occurrence of each id; a refetch can overlap pages when rows shift. */
function uniqueById<T extends { id: string }>(rows: readonly T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

/**
 * Joins the loaded pages of a session into one detail. Session, progress and
 * people come from the newest page; lines are the pages in order, without
 * duplicates.
 */
export function mergeDetailPages(data: InfiniteData<StocktakeDetail, number>): StocktakeDetail {
  const last = data.pages[data.pages.length - 1];
  if (!last) throw new Error('A stocktake detail has at least one page');
  const people: Record<string, string> = {};
  for (const page of data.pages) Object.assign(people, page.people);
  return {
    ...last,
    people,
    lines: { ...last.lines, data: uniqueById(data.pages.flatMap((page) => page.lines.data)), offset: 0 },
  };
}

/** Joins the loaded pages of the session list. */
export function mergeSessionPages(data: InfiniteData<{ data: StocktakeSession[]; total: number }, number>): {
  data: StocktakeSession[];
  total: number;
} {
  const last = data.pages[data.pages.length - 1];
  return { data: uniqueById(data.pages.flatMap((page) => page.data)), total: last?.total ?? 0 };
}

/** Offset of the next page, or undefined when everything is loaded. */
export function nextOffset(loaded: number, total: number, lastPageSize: number): number | undefined {
  return lastPageSize > 0 && loaded < total ? loaded : undefined;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

const keys = {
  all: ['inventory', 'stocktake'] as const,
  list: (status: StocktakeStatus | 'all') => [...keys.all, 'list', status] as const,
  detail: (id: string, filter: StocktakeLineFilter, q: string) => [...keys.all, 'detail', id, filter, q] as const,
  session: (id: string) => [...keys.all, 'detail', id] as const,
  report: (id: string) => [...keys.all, 'report', id] as const,
};

const BASE = '/inventory/stocktake-sessions';

export function useStocktakeSessions(status: StocktakeStatus | 'all') {
  return useInfiniteQuery({
    queryKey: keys.list(status),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<{ data: StocktakeSession[]; total: number }>(BASE, {
        params: { limit: SESSIONS_PAGE_SIZE, offset: pageParam, ...(status === 'all' ? {} : { status }) },
      }),
    getNextPageParam: (last, pages) =>
      nextOffset(
        pages.reduce((loaded, page) => loaded + page.data.length, 0),
        last.total,
        last.data.length,
      ),
    select: mergeSessionPages,
  });
}

/** One page of lines at a time; the filter and the search are part of the key, so changing them starts from page one. */
export function useStocktakeSession(id: string, filter: StocktakeLineFilter, q: string) {
  return useInfiniteQuery({
    queryKey: keys.detail(id, filter, q),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.get<StocktakeDetail>(`${BASE}/${id}`, {
        params: { filter, limit: LINES_PAGE_SIZE, offset: pageParam, ...(q === '' ? {} : { q }) },
      }),
    getNextPageParam: (last, pages) =>
      nextOffset(
        pages.reduce((loaded, page) => loaded + page.lines.data.length, 0),
        last.lines.total,
        last.lines.data.length,
      ),
    select: mergeDetailPages,
  });
}

export function useStocktakeReport(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.report(id),
    queryFn: () => api.get<StocktakeReport>(`${BASE}/${id}/report`),
    enabled,
  });
}

function useInvalidateStocktake() {
  const queryClient = useQueryClient();
  return (options: { stock?: boolean } = {}) => {
    void queryClient.invalidateQueries({ queryKey: keys.all });
    if (options.stock) {
      // Approval writes stock movements; refresh every stock view.
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    }
  };
}

export function useCreateStocktake() {
  const invalidate = useInvalidateStocktake();
  return useMutation({
    mutationFn: (input: CreateStocktakeInput) => api.post<StocktakeSession>(BASE, input),
    onSuccess: () => invalidate(),
  });
}

export function useStocktakeAction(action: 'start' | 'submit' | 'cancel') {
  const invalidate = useInvalidateStocktake();
  return useMutation({
    mutationFn: (input: { id: string; body?: Record<string, unknown> }) =>
      api.post<StocktakeSession>(`${BASE}/${input.id}/${action}`, input.body ?? {}),
    onSuccess: () => invalidate(),
  });
}

export function useRecordCounts(sessionId: string) {
  const invalidate = useInvalidateStocktake();
  return useMutation({
    mutationFn: (counts: CountEntry[]) =>
      api.put<{ data: StocktakeLine[] }>(`${BASE}/${sessionId}/counts`, { counts }),
    onSuccess: () => invalidate(),
  });
}

export function useAddStocktakeLine(sessionId: string) {
  const invalidate = useInvalidateStocktake();
  return useMutation({
    mutationFn: (input: { partId: string; binLocationId?: string | null }) =>
      api.post<StocktakeLine>(`${BASE}/${sessionId}/lines`, input),
    onSuccess: () => invalidate(),
  });
}

export function useApproveStocktake(sessionId: string) {
  const invalidate = useInvalidateStocktake();
  return useMutation({
    mutationFn: (input: ApproveStocktakeInput) =>
      api.post<ApproveStocktakeResult>(`${BASE}/${sessionId}/approve`, input),
    onSuccess: () => invalidate({ stock: true }),
  });
}
